import { useEffect, useState } from "react";
import QRCode from "qrcode";
import DecorSvg from "./DecorSvg";
import { fontCss, CANVAS_W, CANVAS_H } from "@/lib/stationery";

function QrImg({ value, size, color, bg }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!value) { setUrl(""); return; }
    QRCode.toDataURL(value, { margin: 1, width: 320, color: { dark: color || "#111", light: bg || "#ffffff" } })
      .then(setUrl).catch(() => setUrl(""));
  }, [value, color, bg]);
  if (!url) return (
    <div style={{ width: size, height: size, background: "#fff", border: "1px dashed #bbb", display: "flex",
      alignItems: "center", justifyContent: "center", color: "#999", fontSize: 11 }}>QR</div>
  );
  return <img src={url} alt="QR" style={{ width: size, height: size, display: "block" }} />;
}

function LayerView({ l }) {
  if (l.type === "text")
    return (
      <div style={{ fontFamily: fontCss(l.font), fontSize: l.size, color: l.color, textAlign: l.align,
        fontWeight: l.weight, fontStyle: l.italic ? "italic" : "normal", letterSpacing: l.spacing,
        lineHeight: l.lineHeight, whiteSpace: "pre-wrap", width: "100%" }}>{l.text}</div>
    );
  if (l.type === "image")
    return l.src ? (
      <img src={l.src} alt="" style={{ width: "100%", height: "100%", objectFit: l.fit, borderRadius: l.radius, display: "block" }} />
    ) : (
      <div style={{ width: "100%", height: "100%", background: "#e7e3dc", borderRadius: l.radius, display: "flex",
        alignItems: "center", justifyContent: "center", color: "#9a938c", fontSize: 12 }}>Photo</div>
    );
  if (l.type === "shape") {
    const st = { width: "100%", height: "100%", background: l.shape === "line" ? l.fill : (l.shape === "rect" || l.shape === "circle" ? l.fill : "transparent") };
    if (l.shape === "circle") st.borderRadius = "50%";
    if (l.stroke && l.stroke !== "none" && l.shape !== "line") { st.border = `${l.strokeWidth}px solid ${l.stroke}`; st.background = l.fill === "none" ? "transparent" : l.fill; }
    return <div style={st} />;
  }
  if (l.type === "decor") return <DecorSvg motif={l.motif} color={l.color} />;
  if (l.type === "qr") return <QrImg value={l.value} size={l.size} color={l.color} bg={l.bg} />;
  return null;
}

export default function StationeryCanvas({ canvas, scale = 1, interactive = false, selectedId, onSelect, onChange, stageRef }) {
  const dispW = (canvas.w || CANVAS_W) * scale;
  const dispH = (canvas.h || CANVAS_H) * scale;

  const start = (e, l, mode) => {
    if (!interactive) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect?.(l.id);
    const sx = e.clientX, sy = e.clientY, s = { ...l };
    const move = (ev) => {
      const dx = (ev.clientX - sx) / scale, dy = (ev.clientY - sy) / scale;
      if (mode === "drag") onChange?.(l.id, { x: Math.round(s.x + dx), y: Math.round(s.y + dy) });
      else if (l.type === "text") onChange?.(l.id, { w: Math.max(40, Math.round(s.w + dx)) });
      else if (l.type === "qr") onChange?.(l.id, { size: Math.max(40, Math.round(s.size + dx)) });
      else { const ratio = (s.h || 1) / (s.w || 1); const nw = Math.max(20, Math.round(s.w + dx)); onChange?.(l.id, { w: nw, h: Math.max(20, Math.round(nw * ratio)) }); }
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  return (
    <div style={{ width: dispW, height: dispH, position: "relative", overflow: "hidden",
      boxShadow: "0 20px 50px -18px rgba(0,0,0,.35)", borderRadius: 2 }}
      onPointerDown={() => interactive && onSelect?.(null)}>
      <div ref={stageRef} style={{ width: canvas.w || CANVAS_W, height: canvas.h || CANVAS_H, background: canvas.bg,
        position: "absolute", top: 0, left: 0, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        {(canvas.layers || []).map((l) => {
          const sel = interactive && selectedId === l.id;
          const box = {
            position: "absolute", left: l.x, top: l.y,
            width: l.type === "qr" ? l.size : l.w,
            height: l.type === "text" ? "auto" : (l.type === "qr" ? l.size : l.h),
            transform: l.rotation ? `rotate(${l.rotation}deg)` : undefined,
            outline: sel ? "1.5px solid #C08A5B" : "none", cursor: interactive ? "move" : "default",
          };
          return (
            <div key={l.id} style={box} onPointerDown={(e) => start(e, l, "drag")}>
              <LayerView l={l} />
              {sel && (
                <div onPointerDown={(e) => start(e, l, "resize")}
                  style={{ position: "absolute", right: -6, bottom: -6, width: 12, height: 12, background: "#C08A5B",
                    border: "2px solid #fff", borderRadius: "50%", cursor: "nwse-resize" }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
